import i18next from "i18next";
import createFetchClient from "openapi-fetch";
import createQueryClient from "openapi-react-query";
import { default as posthog } from "posthog-js";
import { expireSession, refreshTokenSingleton } from "./auth";
import { getEnvs } from "./env";
import { languageToOldFormat } from "./languages";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "./token";
import { getWhiteLabel } from "./white-label";
import type { paths } from "../generated/api-types";

const { API_ENV } = getEnvs();
const { name, partnerRef, id } = getWhiteLabel();
const refParam = new URLSearchParams(window.location.search).get("ref");

const getHeaders = () => ({
  acceptLanguage: languageToOldFormat(i18next.language),
  distinctId: posthog.get_distinct_id() ?? "",
  hostname: location.hostname,
  partnerRef: refParam || partnerRef,
  whiteLabelId: id?.toString() ?? "",
  whiteLabelName: name,
  authorization: getAccessToken() ? `JWT ${getAccessToken()}` : undefined,
});

export type ApiEnv = "staging" | "production";

const refreshTokensRetryCounts = new Map<string, number>();
const MAX_RETRY_COUNT = 3;

export const urlConfig: Record<ApiEnv, string> = {
  staging: "https://main-site-bff.api-staging-internal.mozio.com",
  production: "https://main-site-bff.api.mozio.com/",
};

const fetchClient = createFetchClient<MakeHeadersOptional<paths>>({
  baseUrl: urlConfig[API_ENV],
});

/**
 * Clones of in-flight requests, so a 401 can be replayed after the token is
 * refreshed. Keyed by the request itself: openapi-fetch hands the same `Request`
 * instance to `onRequest` and `onResponse`, and a WeakMap needs no cleanup.
 *
 * A module-level `let` was wrong here — concurrent requests overwrote each
 * other, so a refresh could replay somebody else's request (DEV-12536).
 */
const originalRequests = new WeakMap<Request, Request>();

fetchClient.use({
  onRequest: async ({ request }) => {
    const headers = getHeaders();
    request.headers.set("Accept-Language", headers.acceptLanguage);
    request.headers.set("Distinct-Id", headers.distinctId);
    request.headers.set("Hostname", headers.hostname);
    request.headers.set("Partner-Ref", headers.partnerRef);
    request.headers.set("White-Label-Id", headers.whiteLabelId);
    request.headers.set("White-Label-Name", headers.whiteLabelName);

    if (headers.authorization) {
      request.headers.set("Authorization", headers.authorization);
      // Cloned after the headers are set, so a replay carries them too. Only
      // authenticated requests are ever replayed, so cloning an anonymous one
      // would just copy its body for nothing.
      originalRequests.set(request, request.clone());
    }

    return request;
  },
  onResponse: async ({ response, request }) => {
    if (response.ok) {
      // A recovered request must not leave its retry budget spent — this page
      // polls one URL for the length of a checkout session.
      refreshTokensRetryCounts.delete(request.url);
      return undefined;
    }

    // Every other failure is the caller's to handle. Returning undefined leaves
    // the response untouched; rewriting it to 200 (as this used to) fed error
    // bodies to onSuccess as if they were data.
    if (response.status !== 401) {
      return undefined;
    }

    // A request that carried no token has no session to expire. This is a
    // backend problem, so let it surface as an ordinary error.
    if (!request.headers.has("Authorization")) {
      return undefined;
    }

    const attempts = refreshTokensRetryCounts.get(request.url) ?? 0;
    if (attempts >= MAX_RETRY_COUNT) {
      console.error("Max retry count reached");
      throw expireSession();
    }
    refreshTokensRetryCounts.set(request.url, attempts + 1);

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      console.error("No refresh token found");
      throw expireSession();
    }

    let result;
    try {
      result = await refreshTokenSingleton(refreshToken);
    } catch {
      // refreshTokenSingleton logs and rethrows.
      throw expireSession();
    }

    if ("error" in result) {
      console.error(result.message);
      throw expireSession();
    }

    setAccessToken(result.accessToken);
    setRefreshToken(result.refreshToken);

    const original = originalRequests.get(request);
    if (!original) {
      console.error("Original request is undefined");
      throw expireSession();
    }

    const retryRequest = original.clone();
    retryRequest.headers.set("Authorization", `JWT ${result.accessToken}`);

    const retryResponse = await fetch(retryRequest);
    if (retryResponse.ok) {
      refreshTokensRetryCounts.delete(request.url);
      // Returned as-is: re-wrapping it dropped headers and coerced 204 to 200.
      return retryResponse;
    }

    if (retryResponse.status === 401) {
      throw expireSession();
    }

    return retryResponse;
  },
});

export const api = createQueryClient(fetchClient);

/**
 * A utility type to make the `header` field optional in the parameters of any API endpoint.
 *
 * We use this because headers are set in middleware, so they don't need to be passed explicitly for every request.
 */
type MakeHeadersOptional<T> = {
  [Path in keyof T]: {
    [Method in keyof T[Path]]: T[Path][Method] extends {
      parameters: { header: infer H };
    }
      ? {
          parameters: Omit<T[Path][Method]["parameters"], "header"> & {
            header?: Partial<H>;
          };
        } & Omit<T[Path][Method], "parameters">
      : T[Path][Method];
  };
};
